import { useUserModalRootActionsStore } from '@/features/users/_shared/components/user-modal-root/user-modal-root.store';
import { memo, useCallback, useMemo, useState } from 'react';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { TTeam } from '@/features/users/team/types/team.type';
import { useForm } from 'react-hook-form';
import { InviteAgentValidationSchema, TInviteAgentValidation, useInviteAgent } from '../../../api/invite-agent.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetListTeamQuery } from '@/features/users/team/api/get-list-team-api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_AGENT_QUERY_KEY } from '../../../api/get-list-agent-api';
import { handleDomBubbleEvent } from '@/utils/dom';
import Input from '@/components/ui/input';
import GenericMultiselectDropdown from '@/components/ui/multiselect-dropdown/generic-multiselect-dropdown';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';
import type { AxiosErrorCustom } from '@/lib/axios';

export type TInviteAgentModalType = 'InviteAgentModal';
export type IInviteAgentModalProps = Record<string, never>;

const InviteAgentModal = memo((_props: IInviteAgentModalProps) => {
  const userModalRootActions = useUserModalRootActionsStore();
  const showToast = useCustomToast();
  const [selectedTeam, setSelectedTeam] = useState<TTeam[]>([]);

  const isOpenInviteAgentModal = useMemo(() => {
    return userModalRootActions.checkIsOpenModal('InviteAgentModal');
  }, [userModalRootActions]);

  const form = useForm<TInviteAgentValidation>({
    mode: 'onChange',
    resolver: zodResolver(InviteAgentValidationSchema),
  });

  /**
   * Get Team
   */
  const { data: teamData, isLoading: isLoadingGetTeam } = useGetListTeamQuery({
    params: {
      search: '',
      page: 1,
      limit: 500,
      sortDir: 'DESC',
      sortBy: 'createdAt',
    },
  });

  const teamOptions = useMemo(() => {
    return teamData?.payload?.rows || [];
  }, [teamData]);

  const mutationInviteAgent = useInviteAgent({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Error invite agent, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_AGENT_QUERY_KEY] })]);
        userModalRootActions.closeModal('InviteAgentModal');
        form.reset();
        setSelectedTeam([]);
        showToast({
          message: 'Invite agent successfully!',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitInviteAgent = useCallback(
    (data: TInviteAgentValidation) => {
      mutationInviteAgent.mutate({
        fullname: data.fullname,
        email: data.email,
        ...(selectedTeam?.length > 0 && {
          teamIds: selectedTeam?.map((team) => team?.teamId) as [string, ...string[]],
        }),
      });
    },
    [selectedTeam, mutationInviteAgent]
  );

  const handleClose = useCallback(() => {
    form.reset();
    setSelectedTeam([]);
    userModalRootActions.closeModal('InviteAgentModal');
  }, [form, userModalRootActions]);

  return (
    <Modal isOpen={isOpenInviteAgentModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[400px]">
      <ModalHeader>
        <ModalTitle>Invite Agent</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitInviteAgent)(e);
          }}
          id="add-agent-form"
          className="grid gap-[16px]"
        >
          <div className="grid gap-[4px]">
            <label htmlFor="fullname" className="text-[#4B4B4B] text-[12px] font-normal">
              Full Name*
            </label>
            <Input id="fullname" placeholder="Enter Full Name" {...form.register('fullname')} />
            {form.formState.errors.fullname && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.fullname.message}</p>
            )}
          </div>
          <div className="grid gap-[4px]">
            <label htmlFor="email" className="text-[#4B4B4B] text-[12px] font-normal">
              Email*
            </label>
            <Input id="email" placeholder="Enter Email" {...form.register('email')} />
            {form.formState.errors.email && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.email.message}</p>
            )}
          </div>
          <div className="grid gap-[4px]">
            <label htmlFor="team" className="text-[#4B4B4B] text-[12px] font-normal">
              Team (Optional)
            </label>
            <GenericMultiselectDropdown
              isLoading={isLoadingGetTeam}
              options={teamOptions}
              value={selectedTeam}
              onChange={(value) => {
                setSelectedTeam(value);
              }}
              labelKey="name"
              valueKey="teamId"
              placeholder="Select Teams"
              searchPlaceholder="Search teams..."
            />
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="add-agent-form" type="submit" disabled={mutationInviteAgent.isPending}>
          {mutationInviteAgent.isPending ? <Spinner className="w-[18px] h-[18px]" /> : null} Create
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

InviteAgentModal.displayName = 'InviteAgentModal';
export default InviteAgentModal;
